// Kopfzeile der Module — überall gleich: drei Striche, Titel, optional eine
// Aktion rechts.
//
// Links stehen bewusst die DREI STRICHE und kein Zurück-Pfeil: Mit ihnen ist die
// Verwaltung von jeder Seite aus einen Tipp entfernt. Der Weg zurück geht
// trotzdem nicht verloren — "Start" steht immer ganz links in der unteren Leiste.
//
// Die Striche gibt es NUR für Admins. Dahinter liegt ausschließlich die
// Verwaltung; für alle anderen wäre das Menü leer, und ein Knopf, der nichts
// zeigt, ist schlimmer als kein Knopf. Ohne ihn rückt der Titel nach links —
// dafür das bedingte Padding.

import { useLocation } from "react-router-dom";
import { useAuth } from "@/stores/authStore";
import { ProfilKnopf } from "./ProfilKnopf";
import { useSidebar } from "./SidebarContext";
import { cn } from "@/design-system/ui";

export function ModulHeader({
  titel,
  untertitel,
  aktion,
  zurueck,
}: {
  titel: string;
  untertitel?: string;
  aktion?: React.ReactNode;
  /**
   * Statt der drei Striche ein Zurück-Pfeil — für Unterseiten wie einen
   * einzelnen Kunden. Die Kopfzeile bleibt dieselbe: gleiche Höhe, gleiche
   * Linie, gleiches Profil rechts. Vorher baute sich jede Unterseite ihren
   * eigenen Kopf, und der sah überall anders aus.
   */
  zurueck?: () => void;
}) {
  const { oeffnen } = useSidebar();
  const istAdmin = useAuth((s) => s.role) === "admin";
  const aufProfilSeite = useLocation().pathname.startsWith("/crm/profil");
  const linksLeer = !zurueck && !istAdmin;

  return (
    <header
      className={cn(
        "flex shrink-0 items-center gap-3 border-b border-ink-700/70 py-3 pr-4",
        linksLeer ? "pl-5" : "pl-4"
      )}
    >
      {zurueck && (
        <button
          onClick={zurueck}
          aria-label="Zurück"
          className="-ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-ink-400 transition-colors duration-fast hover:bg-ink-800 hover:text-ink-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
      )}
      {/* Nur EIN Knopf links: Auf einer Unterseite führt der Weg zurück, nicht
          ins Menü. Beide nebeneinander wäre eine Wahl, die niemand treffen will. */}
      {istAdmin && !zurueck && (
        <button
          onClick={oeffnen}
          aria-label="Menü öffnen"
          data-testid="menu-button"
          className="-ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-ink-400 transition-colors duration-fast hover:bg-ink-800 hover:text-ink-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
            <g stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
              <path d="M4 7h16" />
              <path d="M4 12h16" />
              <path d="M4 17h16" />
            </g>
          </svg>
        </button>
      )}

      <div className="min-w-0 flex-1">
        <h1 className="truncate text-[15px] font-semibold text-ink-50">{titel}</h1>
        {untertitel && (
          <p className="mt-0.5 truncate text-sm tabular-nums text-ink-400">{untertitel}</p>
        )}
      </div>

      {/* Seitenaktion zuerst, das Profil ganz aussen — so bleibt es auf JEDER
          Seite an derselben Stelle, egal ob es daneben einen Knopf gibt.
          Auf der Profilseite selbst wäre es ein Link auf sich selbst; dort
          verzichten wir darauf. */}
      {aktion && <div className="shrink-0">{aktion}</div>}
      {!aufProfilSeite && <ProfilKnopf className="-mr-2 shrink-0" />}
    </header>
  );
}
